import { useEffect } from "react";
import { QueryClient, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";
import { useMessagePopupStore } from "shared/app/MessagePopup/store";
import { inboxApi } from "./api";
import { inboxKeys } from "./hooks";
import {
  subscribeInboxSocketEvents,
  useInboxEventsSocket
} from "./inboxSocketEvents";
import { useInboxToken } from "./useInboxToken";

const POLL_INTERVAL_MS = 60_000;
const REFRESH_DEBOUNCE_MS = 1_000;

const EMPTY_BADGE = { total: 0, awaiting_reply: 0, escalated: 0 };

const liveRefreshByClient = new WeakMap<
  QueryClient,
  { holders: number; release: () => void }
>();

const subscribeBadgeRefresh = (queryClient: QueryClient) => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const unsubscribe = subscribeInboxSocketEvents(() => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      queryClient.invalidateQueries({ queryKey: inboxKeys.badge() });
    }, REFRESH_DEBOUNCE_MS);
  });
  return () => {
    unsubscribe();
    clearTimeout(timer);
  };
};

const acquireBadgeRefresh = (queryClient: QueryClient) => {
  const shared = liveRefreshByClient.get(queryClient) ?? {
    holders: 0,
    release: subscribeBadgeRefresh(queryClient)
  };
  shared.holders += 1;
  liveRefreshByClient.set(queryClient, shared);

  return () => {
    shared.holders -= 1;
    if (shared.holders > 0) return;
    shared.release();
    liveRefreshByClient.delete(queryClient);
  };
};

export const useInboxBadge = (enabled = true) => {
  const queryClient = useQueryClient();
  const { accessToken } = useInboxToken();
  const isMessagePopupOpen = useMessagePopupStore(
    (state) => state.target !== null
  );
  const isOnInboxPage = useLocation().pathname.startsWith("/inbox");

  useInboxEventsSocket({
    accessToken,
    enabled: enabled && !isMessagePopupOpen && !isOnInboxPage
  });

  useEffect(() => {
    if (!enabled) return;
    return acquireBadgeRefresh(queryClient);
  }, [enabled, queryClient]);

  const query = useQuery({
    queryKey: inboxKeys.badge(),
    queryFn: () => inboxApi.getInboxBadge(),
    enabled: enabled && !!accessToken,
    refetchInterval: POLL_INTERVAL_MS,
    refetchOnWindowFocus: true,
    retry: 1
  });

  return { ...query, badge: query.data ?? EMPTY_BADGE };
};
